import { useEffect, useState } from 'react';
import type { GameState } from '@flip7/shared';
import { useSound } from '../hooks/useSound';

interface GameStartScreenProps {
  gameState: GameState;
  onComplete: () => void;
}

export function GameStartScreen({ gameState, onComplete }: GameStartScreenProps) {
  const play = useSound();
  const [phase, setPhase] = useState<'title' | 'players' | 'round'>('title');
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    play('flip7');

    // Title phase: 800ms
    const t1 = setTimeout(() => setPhase('players'), 800);

    // Reveal players one by one, 300ms apart
    const playerTimers: ReturnType<typeof setTimeout>[] = [];
    gameState.players.forEach((_, i) => {
      playerTimers.push(
        setTimeout(() => {
          setRevealedCount(i + 1);
          play('cardTap');
        }, 800 + (i + 1) * 300),
      );
    });

    // Round reveal phase
    const playersTime = 800 + (gameState.players.length + 1) * 300;
    const t2 = setTimeout(() => {
      setPhase('round');
      play('roundEnd');
    }, playersTime);

    // Auto-dismiss
    const t3 = setTimeout(onComplete, playersTime + 1200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      playerTimers.forEach(clearTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="screen screen-center ceremony-overlay"
      onClick={onComplete}
      role="status"
      aria-label="Game starting"
    >
      <h1 className="title-logo round-reveal ceremony-overlay__title">
        FLIP 7
      </h1>

      {(phase === 'players' || phase === 'round') && (
        <div className="ceremony-overlay__players">
          {gameState.players.slice(0, revealedCount).map((player, i) => (
            <span
              key={player.id}
              className="badge-pop ceremony-overlay__player"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              {player.name}
            </span>
          ))}
        </div>
      )}

      {phase === 'round' && (
        <div className="round-reveal ceremony-overlay__round">
          <p className="ceremony-overlay__round-number">
            Round {gameState.currentRound}
          </p>
          <p className="ceremony-overlay__round-target">
            First to {gameState.targetScore} wins
          </p>
        </div>
      )}

      <p className="ceremony-overlay__skip">
        Tap to skip
      </p>
    </div>
  );
}
